import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import EmptyState from '../../components/EmptyState/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert/ErrorAlert';
import Loader from '../../components/Loader/Loader';
import Page from '../../components/Page/Page';
import Panel from '../../components/Panel/Panel';
import Badge from '../../components/Styled/Badge';
import TextInput from '../../components/TextInput/TextInput';
import { LiveStream } from '../../graphql/graphql';
import {
  BAN_USER_FROM_LIVE_STREAMING,
  END_LIVE_STREAM,
} from '../../graphql/mutations-gql';
import { ACTIVE_LIVE_STREAMS, GET_LIVE_STREAM } from '../../graphql/queries-gql';

const StreamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StreamCard = styled.article`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 24px;
  padding: 16px;
  background: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 12px;
`;

const Thumbnail = styled.div`
  position: relative;
  width: 132px;
  height: 88px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background: ${(props) => props.theme.surfaceRaised};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 260px;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const StreamTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
`;

const HostRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const HostAvatar = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: ${(props) => props.theme.surfaceRaised};
`;

const UserLink = styled(Link)`
  color: ${(props) => props.theme.textColor};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Mono = styled.span`
  font-family: ${(props) => props.theme.monoFontFamily};
  font-size: 11.5px;
  color: ${(props) => props.theme.mutedTextColor};
  overflow-wrap: anywhere;
`;

const Description = styled.span`
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};
`;

// Fixed tracks so the numbers line up from one stream to the next.
const Stats = styled.dl`
  display: grid;
  grid-template-columns: repeat(4, 84px);
  gap: 10px 16px;
  margin: 0;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(2, 84px);
  }
`;

const Stat = styled.div<{ $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  ${(props) => (props.$wide ? 'grid-column: span 2;' : '')}

  dt {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${(props) => props.theme.mutedTextColor};
  }

  dd {
    margin: 0;
    font-size: 13.5px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: ${(props) => props.theme.textColor};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: auto;
`;

const SearchRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleString();
};

const formatDuration = (startedAt: string): string => {
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  const seconds = Math.floor((now - start) / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

const LiveStreams = () => {
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [fetchLiveStream, { data: searchData, loading: searchLoading, error: searchError }] =
    useLazyQuery<{ getLiveStream: LiveStream | null }>(GET_LIVE_STREAM, {
      fetchPolicy: 'network-only',
    });

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    fetchLiveStream({ variables: { streamId: trimmed } });
  };

  const { data, loading, error, refetch } = useQuery(ACTIVE_LIVE_STREAMS, {
    variables: { limit: 100 },
    fetchPolicy: 'network-only',
    pollInterval: 10000,
  });

  const [endLiveStream] = useMutation(END_LIVE_STREAM);
  const [banUserFromLiveStreaming] = useMutation(BAN_USER_FROM_LIVE_STREAMING);

  const handleEndStream = async (streamId: string) => {
    if (!window.confirm('Are you sure you want to end this live stream?')) return;
    setActionLoading(streamId);
    try {
      await endLiveStream({ variables: { streamId } });
      await refetch();
    } catch (e) {
      console.error('Failed to end live stream:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBanUser = async (streamId: string, userId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to ban this user from live streaming? This will also end the current stream.'
      )
    )
      return;
    setActionLoading(streamId + '-ban');
    try {
      await banUserFromLiveStreaming({ variables: { streamId, userId } });
      await refetch();
    } catch (e) {
      console.error('Failed to ban user from live streaming:', e);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading && !data) {
    return (
      <Page title="Live streams">
        <Loader />
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Live streams">
        <ErrorAlert error={error} />
      </Page>
    );
  }

  const streams: LiveStream[] = data?.getActiveLiveStreams?.nodes || [];

  // One row per stream. Used for both the active list and the search result,
  // which can also be an ended stream (then there is nothing to end or ban).
  const renderStream = (stream: LiveStream) => {
    const isLive = stream.status === 'LIVE';

    return (
      <StreamCard key={stream._id}>
        {stream.thumbnailUrl && (
          <Thumbnail>
            <img src={stream.thumbnailUrl} alt={stream.title || 'Live stream'} />
          </Thumbnail>
        )}

        <Identity>
          <TitleRow>
            <StreamTitle>{stream.title || 'Untitled stream'}</StreamTitle>
            <Badge $tone={isLive ? 'red' : 'neutral'}>{stream.status}</Badge>
            {stream.isPrivate && <Badge $tone="purple">Private</Badge>}
          </TitleRow>
          <HostRow>
            <HostAvatar
              src={stream.hostUser.profilePhotoThumbnailUrl}
              alt=""
            />
            <UserLink to={`/user/${stream.hostUser.id}`}>
              {stream.hostUser.name}
            </UserLink>
            <Mono>{stream.hostUser.id}</Mono>
          </HostRow>
          {stream.description && <Description>{stream.description}</Description>}
          <Mono>Stream {stream._id}</Mono>
        </Identity>

        <Stats>
          <Stat>
            <dt>Viewers</dt>
            <dd>{stream.currentViewerCount}</dd>
          </Stat>
          <Stat>
            <dt>Peak</dt>
            <dd>{stream.peakViewerCount}</dd>
          </Stat>
          <Stat>
            <dt>Diamonds</dt>
            <dd>{stream.totalDiamondsEarned}</dd>
          </Stat>
          <Stat>
            <dt>Duration</dt>
            <dd>
              {stream.startedAt && isLive
                ? formatDuration(stream.startedAt)
                : '-'}
            </dd>
          </Stat>
          <Stat>
            <dt>Comments</dt>
            <dd>{stream.allowComments ? 'On' : 'Off'}</dd>
          </Stat>
          <Stat>
            <dt>Gifts</dt>
            <dd>{stream.allowGifts ? 'On' : 'Off'}</dd>
          </Stat>
          <Stat $wide>
            <dt>Started</dt>
            <dd>{stream.startedAt ? formatDate(stream.startedAt) : '-'}</dd>
          </Stat>
          {stream.endedAt && (
            <Stat $wide>
              <dt>Ended</dt>
              <dd>{formatDate(stream.endedAt)}</dd>
            </Stat>
          )}
        </Stats>

        {isLive && (
          <Actions>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => handleEndStream(stream._id)}
              disabled={actionLoading === stream._id}
            >
              {actionLoading === stream._id ? 'Ending...' : 'End stream'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleBanUser(stream._id, stream.hostUser.id)}
              disabled={actionLoading === stream._id + '-ban'}
            >
              {actionLoading === stream._id + '-ban'
                ? 'Banning...'
                : 'Ban from streaming'}
            </Button>
          </Actions>
        )}
      </StreamCard>
    );
  };

  return (
    <Page
      title="Live streams"
      description="Active streams refresh every 10 seconds."
      badge={
        <Badge $tone={streams.length > 0 ? 'red' : 'neutral'}>
          {streams.length} live
        </Badge>
      }
      actions={
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<ArrowPathIcon width={15} />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      }
    >
      <Panel title="Find a stream by ID">
        <SearchRow>
          <div style={{ maxWidth: 420 }}>
            <TextInput
              placeHolder="Enter live stream ID and press Enter"
              icon={<MagnifyingGlassIcon width={16} />}
              onSubmit={handleSearch}
            />
          </div>
          {searchLoading && <Loader />}
          {searchError && <ErrorAlert error={searchError} />}
          {searchData && !searchData.getLiveStream && !searchLoading && (
            <Description>No live stream found with that ID.</Description>
          )}
          {searchData?.getLiveStream && renderStream(searchData.getLiveStream)}
        </SearchRow>
      </Panel>

      {streams.length > 0 ? (
        <StreamList>{streams.map((stream) => renderStream(stream))}</StreamList>
      ) : (
        <EmptyState
          Icon={SignalIcon}
          title="No active live streams"
          description="Streams appear here as soon as someone goes live."
        />
      )}
    </Page>
  );
};

export default LiveStreams;
